import { appState, farmScreenOff, setSelectedFarm } from '../core/state';
import { can, isAdmin as isAdminRole, currentRole } from '../core/access';
import { inlineStreams, stopInlineStream } from '../stream/inline-stream';
import { refreshIconSvg } from '../stream/stream-controls-shared';
import { makeDeviceCard } from '../devices/device-card';
import { createDeviceGrid, cardSizeControlsHtml, wireCardSizeControls, applyCardSizeVars, invalidateDeviceGridCache, seedDeviceGridCache, diffDeviceGrid, } from '../devices/device-grid';
import { isAggregatorFarm, isDuoFarm, isGeelarkFarm, isAdspowerFarm, isMercadoApiFarm, isCloudPhoneFarm } from '../core/farm-kind';
import { makeFarmRow, createFarmFilterState, buildFarmFilter, applyFarmFilter } from '../core/farm-list';
import { navigate, parseRouteFromHash } from '../core/router';
import { onClickerDevicesChange } from '../clicker/clicker-view';
import { onWorkspaceDevicesChange } from '../workspace/workspace-view';
import { inWorkspace } from '../workspace/workspace-store';
import { getClickerRunning, refreshClickerRunning } from '../clicker/clicker-shared';
import { showAddFarmModal } from './farm-modals';
import { showAddDeviceModal, injectAggregatorPendingCards } from './aggregator-devices';
import { startDuoDevicePolling, stopDuoDevicePolling, injectDuoDevices } from './duo-devices';
import { startGeelarkDevicePolling, stopGeelarkDevicePolling, injectGeelarkDevices } from './geelark-devices';
import { startAdspowerDevicePolling, stopAdspowerDevicePolling } from './adspower-devices';
import { startMercadoDevicePolling, stopMercadoDevicePolling } from './mercado-devices';
import { sortBySavedOrder, wireGridDragReorder, isReordering } from './device-order';
import { t } from '../ui/lang';
// Match a device to a farm by its STABLE id, so a farm rename never detaches the
// device grid from its phones. Falls back to the name only when the id isn't known
// yet — a device the relay hasn't re-stamped, or a first paint before the farm id
// resolved (see resolveFarmRecordById). Pass the id whenever you have it (farm rows
// carry f.id; the grid tracks appState.selectedFarmId) and the name as the fallback.
export function deviceInFarm(d, farmId, farmName) {
    if (farmId != null && d.farmId != null)
        return d.farmId === farmId;
    return !!farmName && d.farmName === farmName;
}
export function farmDevices(devices, farmId, farmName) {
    const out = [];
    for (const d of devices.values())
        if (deviceInFarm(d, farmId, farmName))
            out.push(d);
    // Apply the operator's saved manual order so the grid is stable across navigation
    // (and reorderable by drag & drop). Unknown/new devices stay at the end.
    return sortBySavedOrder(farmId, out, (d) => d.udid);
}
// Resolve a farm record by name for the device-grid header (to decide whether it's
// an aggregator farm and get its id for the "Add device" flow). Uses the last farms
// fetch when present, else a small per-name cache, else a one-off fetch — kind + id
// are stable, so this stays cheap across poll-driven grid rebuilds.
const farmRecordByName = new Map();
const farmRecordById = new Map();
function cacheFarmRecords(list) {
    for (const f of list) {
        farmRecordByName.set(f.name, f);
        farmRecordById.set(f.id, f);
    }
}
// Routing is by the stable farm id; these resolve it back to the record/name the
// display layer still uses. Sync lookups read the caches the farms poll / prior
// resolves populated; farmNameById returns undefined only on a cold deep-link before
// the first /api/farms fetch, where the async resolveFarmRecordById fills it in.
function farmRecordByIdSync(id) {
    return lastFarmsRecords?.find((f) => f.id === id) ?? farmRecordById.get(id);
}
export function farmNameById(id) {
    return farmRecordByIdSync(id)?.name;
}
export function isDuoFarmById(id) {
    return isDuoFarm(farmRecordByIdSync(id)?.farm_kind);
}
export async function resolveFarmRecordById(id) {
    const sync = farmRecordByIdSync(id);
    if (sync)
        return sync;
    const list = await fetchFarmsList();
    cacheFarmRecords(list);
    return list.find((f) => f.id === id);
}
/** The farms the current user may see; empty on any failure (the list just shows none). */
async function fetchFarmsList() {
    try {
        const res = await fetch('/api/farms');
        if (res.ok)
            return await res.json();
    }
    catch { /* show empty */ }
    return [];
}
/** Manual refresh of the landing list: a full re-render, which re-fetches /api/farms and
 *  the running-clicker counts. Disabled while it runs so a double click is not two fetches. */
function wireFarmsRefresh() {
    const btn = document.getElementById('farms-refresh-btn');
    btn?.addEventListener('click', () => {
        btn.disabled = true;
        void renderFarms();
    });
}
// A render in flight: the poll must not start a second one on top of it (the first
// device snapshot used to land mid-render and rebuild the page a second time).
let farmsRenderInFlight = false;
export async function renderFarms() {
    // Any explicit (non-poll) render rebuilds the toolbar + list; drop the poll cache
    // so the next poll diffs against this DOM, not a stale snapshot.
    invalidateFarmsListCache();
    farmsRenderInFlight = true;
    try {
        await renderFarmsNow();
    }
    finally {
        farmsRenderInFlight = false;
    }
}
async function renderFarmsNow() {
    // Leaving a farm grid for the list drops its streams — except those of devices the
    // operator took into work. Otherwise stepping through the farm list to pick up one
    // more phone would kill every stream open on the My Devices board, which is exactly
    // the tab-juggling the board exists to end.
    for (const udid of inlineStreams.keys()) {
        if (!inWorkspace(udid))
            stopInlineStream(udid);
    }
    const content = document.getElementById('content');
    const pageTitle = document.getElementById('page-title');
    pageTitle.textContent = t('farms.title');
    // Everything the page needs is fetched up front and the DOM assembled off-screen,
    // then swapped in once. Painting the toolbar, then the rows, then the filter bar as
    // each request landed made the list visibly build itself in stages.
    const [farms] = await Promise.all([fetchFarmsList(), refreshClickerRunning()]);
    // Keep the inline-stream connect path's view of each farm's screen-off
    // preference current from the same fetch.
    for (const f of farms)
        farmScreenOff.set(f.name, !!f.screen_off_on_connect);
    const isAdmin = isAdminRole();
    // Team chips are superadmin-only (they alone manage multiple teams); the rest of
    // the filter bar (search / online / type / version) is for everyone.
    const showTeams = currentRole() === 'superadmin';
    const addBtn = isAdmin ? `<button class="add-user-btn" id="add-farm-btn">${t('farms.add_farm_btn')}</button>` : '';
    // Same icon button as a cloud farm's device grid uses, for the same reason: the list
    // is refreshed when the operator asks for it, not on a timer.
    const refreshBtn = `<button class="tbl-btn farm-refresh-btn" id="farms-refresh-btn" title="${t('common.refresh')}" aria-label="${t('common.refresh')}">${refreshIconSvg}</button>`;
    const toolbar = `<div class="users-toolbar">${addBtn}${refreshBtn}</div><div class="farm-filter" id="farms-filter"></div>`;
    const staging = document.createElement('div');
    staging.innerHTML = farms.length === 0
        ? `${toolbar}<div class="clicker-farm-list"><p class="empty">${t('farms.none_configured')}</p></div>`
        : `${toolbar}<div class="clicker-farm-list" id="farm-list"></div>`;
    const list = staging.querySelector('#farm-list');
    if (list) {
        // Per-farm running-clicker counts (admin endpoint; empty for non-admins) so each
        // row shows "N running" next to the online/devices tally — same as the Clicker list.
        const runningCounts = getClickerRunning().counts;
        for (const f of farms) {
            const { online: rowOnline } = farmRowState(f);
            list.appendChild(makeFarmRow(f, {
                online: rowOnline,
                meta: farmRowMeta(f, runningCounts[f.id] ?? 0),
                lastSeen: f.last_seen,
                onClick: () => {
                    setSelectedFarm(f.name, f.id);
                    navigate({ page: 'farms', farmId: f.id });
                },
            }));
        }
        // Enforce the current filter on the freshly-built rows (selection survives
        // re-renders), then build the filter bar wired to re-apply on change.
        applyFarmFilter(list, farmsFilter);
        const filterEl = staging.querySelector('#farms-filter');
        if (filterEl)
            await buildFarmFilter(filterEl, farmsFilter, farms, { showTeams }, () => applyFarmFilter(list, farmsFilter));
    }
    content.replaceChildren(...staging.childNodes);
    if (isAdmin)
        document.getElementById('add-farm-btn').addEventListener('click', () => showAddFarmModal());
    wireFarmsRefresh();
    seedFarmsListCache(farms);
}
// --- Poll-driven re-render guard for the farms landing list --------------------
//
// onDevicesChange (the periodic device-list WS poll) used to call renderFarms() on
// every tick when sitting on the farms landing page, blowing away content.innerHTML
// — the whole toolbar (the "+ Add farm" button and the "Teams" selector pills) plus
// the farm rows — and rebuilding it from scratch. That made the teams selector bar
// visibly flicker/repaint even when nothing had changed. Mirroring the device-grid
// guard above, we cache a structure key of the fields that actually shape the
// toolbar + rows and skip the rebuild when the poll didn't change any of them.
//
// The key covers the toolbar gating (admin / team-filter visibility) and, per farm,
// everything the rows render: id/name/team/meta plus the *live* online state and
// online count (derived from appState.allDevices, which is what the poll mutates).
// Farm identity/membership only changes via explicit add/rename/delete, all of which
// call renderFarms() directly — that resets the cache, so the poll can never early-
// exit against a list it didn't itself build.
// Last fetched farm records + the structure key the current DOM reflects, so the
// poll can recompute the key from live device state without an extra /api/farms call.
let lastFarmsRecords = null;
let lastFarmsListKey = null;
// Live online/count state for one farm's row, derived from the current device map
// (what polls move). Host farms gate on the agent heartbeat (`f.online`): if the
// agent is offline the farm is offline and its device online-count reads 0.
// Aggregator farms have no agent heartbeat, so they're online whenever at least one
// remote phone is connected, and their count is simply the live online devices.
// Duo farms have no relay at all — their phones never appear in appState.allDevices
// (they come from the separate /duo-devices sync), so the live map would always read
// 0. The server already computes their state from the DuoPlus set (`f.online` =
// deviceCount > 0; `device_count` = the active phones), so use those directly.
export function farmRowState(f) {
    // Mercado API: the "online" accounts are the ones the runner is polling.
    if (isMercadoApiFarm(f.farm_kind)) {
        return { online: f.online, count: f.mercado_running ?? 0 };
    }
    // AdsPower: profiles never reach the live device map; online = browser open.
    if (isAdspowerFarm(f.farm_kind)) {
        return { online: f.online, count: f.adspower_open ?? 0 };
    }
    // Duo/GeeLark: device_count is the synced (ADB-enabled, powered-on) set; the farm is
    // online when it has any. Their phones never appear in the live device map.
    if (isCloudPhoneFarm(f.farm_kind)) {
        return { online: f.online, count: f.online ? (f.device_count ?? 0) : 0 };
    }
    const liveOnline = [...appState.allDevices.values()]
        .filter((d) => deviceInFarm(d, f.id, f.name) && d.state === 'device').length;
    if (isAggregatorFarm(f.farm_kind)) {
        return { online: liveOnline > 0, count: liveOnline };
    }
    return { online: f.online, count: f.online ? liveOnline : 0 };
}
// Single source of truth for a farm row's right-aligned meta text, shared by the Farms
// list and the Clicker list so both read identically: "<N> online · <R> running · <M>
// devices" (the "· R running" segment is omitted when the farm has no running clickers,
// e.g. for non-admins whose /clicker/running is empty). `running` is that farm's live
// running-clicker count (0 → segment hidden).
export function farmRowMeta(f, running) {
    const { count: onlineCount } = farmRowState(f);
    // The online set and the "devices" total come from two different sources (the live
    // device map / DuoPlus active set vs. the persisted farm_devices count / DuoPlus
    // account total), so a poll skew or an under-reporting DuoPlus account-total call
    // can leave online > total — which reads as broken ("6 online · 3 devices"). Clamp
    // the total up to the online count so the row is always internally consistent.
    const deviceTotal = Math.max(onlineCount, farmDeviceTotal(f));
    return `${t('farms.meta_online', { count: onlineCount })}${running ? ` · ${t('farms.meta_running', { count: running })}` : ''} · ${t('farms.meta_devices', { count: deviceTotal })}`;
}
// The "devices" total shown after "N online ·" in a farm row. For duo farms this is the
// whole DuoPlus account (server `duo_total`), so the tally reads "<active> online ·
// <account total> devices"; the active/online set is device_count. Other kinds show
// device_count (their online set and total coincide on the list). Falls back to
// device_count while duo_total is still null (farm not synced yet).
function farmDeviceTotal(f) {
    if (isDuoFarm(f.farm_kind))
        return f.duo_total ?? f.device_count;
    return f.device_count;
}
// Stable, order-sensitive key of the fields that drive the toolbar + farm rows.
function farmsListKey(farms) {
    const header = `${isAdminRole() ? 1 : 0}|${currentRole() === 'superadmin' ? 1 : 0}`;
    const runningCounts = getClickerRunning().counts;
    const parts = farms.map((f) => {
        const live = farmRowState(f);
        // NB: last_seen is deliberately NOT in this structural key. Cloud-phone farms
        // (duo/geelark) bump last_seen on every 30–60s sync, so including it made the whole
        // list rebuild (and visibly flicker) on each poll even when nothing else changed.
        // The "last ping" clock now refreshes on any real structural change or on navigation.
        return `${f.id}|${f.name}|${f.team_id ?? ''}|${f.team_name ?? ''}|${f.location ?? ''}|${f.farm_type ?? 'regular'}|${f.farm_kind ?? 'host'}|${f.farm_version ?? ''}|${f.latest_version ?? ''}|${f.update_available ? 1 : 0}|${f.device_count}|${farmDeviceTotal(f)}|${live.online ? 1 : 0}|${live.count}|${runningCounts[f.id] ?? 0}`;
    });
    return `${header}\n${parts.join('\n')}`;
}
// Force the next onDevicesChange poll to do a full farms-list rebuild.
export function invalidateFarmsListCache() {
    lastFarmsRecords = null;
    lastFarmsListKey = null;
}
// Record the snapshot the current farms-list DOM reflects, so the next poll can diff.
function seedFarmsListCache(farms) {
    lastFarmsRecords = farms;
    lastFarmsListKey = farmsListKey(farms);
    cacheFarmRecords(farms);
}
// Poll entry point: re-render the farms landing list only when the structure-relevant
// fields actually changed; otherwise leave the toolbar (and its team pills) untouched.
function onFarmsListPoll() {
    if (farmsRenderInFlight)
        return; // the build under way reads the same live state
    if (lastFarmsRecords === null || lastFarmsListKey === null) {
        void renderFarms(); // no valid cache (first paint / post-mutation) — full build
        return;
    }
    const key = farmsListKey(lastFarmsRecords);
    if (key === lastFarmsListKey)
        return; // nothing changed
    // The records are the ones the DOM was built from, so a changed key means only the
    // live bits moved (online, counts, running): patch the rows rather than rebuild.
    if (patchFarmRows(lastFarmsRecords)) {
        lastFarmsListKey = key;
        return;
    }
    void renderFarms(); // a row is missing — rebuild + reseed
}
// Update each row's online dot, filter state and meta text in place. False when a row
// cannot be found, which means the DOM is not the list the cache describes.
function patchFarmRows(farms) {
    const list = document.getElementById('farm-list');
    if (!list)
        return false;
    const runningCounts = getClickerRunning().counts;
    for (const f of farms) {
        const row = list.querySelector(`.clicker-farm-row[data-farm-id="${f.id}"]`);
        const dot = row?.querySelector('.status-dot');
        const meta = row?.querySelector('.clicker-farm-row__meta');
        if (!row || !dot || !meta)
            return false;
        const { online } = farmRowState(f);
        dot.classList.toggle('status-dot--green', online);
        dot.classList.toggle('status-dot--red', !online);
        row.dataset['online'] = online ? '1' : '0';
        meta.textContent = farmRowMeta(f, runningCounts[f.id] ?? 0);
    }
    applyFarmFilter(list, farmsFilter);
    return true;
}
// The farms landing list does NOT refresh itself on a timer: the operator reads it to
// pick a farm, and a list that rebuilds under the cursor (versions, duo totals, running
// counts — all server-computed) is worse than a stale one. The Refresh button in the
// toolbar re-fetches on demand; renderFarms() is the whole refresh.
// Client-side farm-list filter (search / online / type / version / team). Module-
// level so the selection survives re-renders (e.g. on a background device update).
const farmsFilter = createFarmFilterState();
// --- Poll-driven re-render guard for the device grid ---------------------------
//
// Every device-list WS message (the periodic farm poll) lands in onDevicesChange,
// which used to rebuild the whole grid — recreating every card (and its toolbar of
// icon buttons) on each poll even when nothing relevant changed. That made the icon
// row visibly flicker/reflow. We instead cache a snapshot of the fields that
// actually shape the cards' icon row and only do a full rebuild when it changes.
//
// The "structure" snapshot covers everything that alters which icons render and
// their enabled state: the device set/order, online state, farm assignment and the
// resolved display name (model + alias). It deliberately excludes `updatedAt`, which
// only changes the per-card clock text — that we patch in place (clock-only path)
// without touching the icon buttons.
// Aggregator farms (phone clients) can't stream video — their cards are adb/clicker
// only and their devices are provisioned rows (so each gets a delete/revoke button
// carrying the farm id). The record is resolved synchronously from the last farms
// snapshot/cache; if it isn't known yet the card falls back to the video layout and
// self-corrects on the next poll-driven rebuild.
function resolveCardOpts(displayName) {
    const farmRec = lastFarmsRecords?.find((f) => f.name === displayName) ?? farmRecordByName.get(displayName);
    // Aggregator phones get a per-phone delete/revoke button; they stream video the
    // same as host devices, so there's no video gating here anymore.
    const aggregatorFarmId = isAggregatorFarm(farmRec?.farm_kind) ? farmRec?.id : undefined;
    return aggregatorFarmId != null ? { aggregatorFarmId } : {};
}
export function renderFarmDevices() {
    // Any explicit (non-poll) render builds a fresh grid; drop the poll cache so the
    // next poll diffs against this DOM, not a stale snapshot.
    invalidateDeviceGridCache();
    const content = document.getElementById('content');
    const pageTitle = document.getElementById('page-title');
    // Prefer the stable farm id (survives a rename); fall back to the name until the
    // id is known. The async resolveFarmRecordById below adopts the resolved name and
    // re-renders on a cold deep-link where only the id was known at paint time.
    const devs = farmDevices(appState.allDevices, appState.selectedFarmId, appState.selectedFarm);
    const displayName = appState.selectedFarm || t('farms.unknown_farm');
    pageTitle.textContent = displayName;
    // This render replaces #device-grid. If a duo farm's screen poll is running for a
    // DIFFERENT farm, tear it down now (its loops must not outlive the switch); a
    // re-render of the SAME duo farm is kept alive so its live screens survive the
    // rebuild instead of snapping back to "Not connected".
    stopDuoDevicePolling(displayName);
    stopGeelarkDevicePolling(displayName);
    stopAdspowerDevicePolling(displayName);
    stopMercadoDevicePolling(displayName);
    const settingsBtn = can('farm.deviceAccess')
        ? `<button class="tbl-btn farm-settings-btn" id="farm-settings-btn" title="${t('farms.settings_btn_title')}">
               <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                   <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
               </svg>
               ${t('common.settings')}
           </button>`
        : '';
    // Cloud-phone farms (duo/geelark) serve the grid from a background sync, so give them a
    // manual Refresh button that forces a fresh server sync (?refresh=1) instead of waiting
    // for the poll. Kind is resolved from the cached record; hidden if not yet known.
    const cloudKind = farmRecordByIdSync(appState.selectedFarmId ?? -1)?.farm_kind;
    const refreshBtn = (isDuoFarm(cloudKind) || isGeelarkFarm(cloudKind))
        ? `<button class="tbl-btn farm-refresh-btn" id="farm-refresh-btn" title="${t('farms.refresh_devices')}" aria-label="${t('farms.refresh_devices')}">${refreshIconSvg}</button>`
        : '';
    content.innerHTML = `
        <div class="farm-devs-header">
            <button class="back-btn" id="back-btn">${t('farms.back_to_farms')}</button>
            ${refreshBtn}
            ${cardSizeControlsHtml()}
            ${settingsBtn}
        </div>
    `;
    // Grid chrome (scrolling area + floating arrows + the size sliders' wiring) comes
    // from the shared engine, so the dashboard renders an identical one.
    const { wrap, grid, updateArrows } = createDeviceGrid({ id: 'device-grid' });
    content.appendChild(wrap);
    applyCardSizeVars(grid);
    wireCardSizeControls(grid, updateArrows);
    document.getElementById('back-btn').addEventListener('click', () => {
        navigate({ page: 'farms' });
    });
    document.getElementById('farm-settings-btn')?.addEventListener('click', () => {
        if (appState.selectedFarmId != null)
            navigate({ page: 'farms', farmId: appState.selectedFarmId, settings: true });
    });
    document.getElementById('farm-refresh-btn')?.addEventListener('click', () => {
        const fid = appState.selectedFarmId;
        if (fid == null)
            return;
        const btn = document.getElementById('farm-refresh-btn');
        if (btn)
            btn.disabled = true;
        const done = () => { if (btn)
            btn.disabled = false; };
        const k = farmRecordByIdSync(fid)?.farm_kind;
        if (isDuoFarm(k))
            void injectDuoDevices(fid, { refresh: true }).finally(done);
        else if (isGeelarkFarm(k))
            void injectGeelarkDevices(fid, { refresh: true }).finally(done);
        else
            done();
    });
    // The farm's kind/id may not be known synchronously on first paint (e.g. navigated
    // straight here before the farms list loaded), so aggregator devices fall back to
    // the video layout ("Not connected") and only self-correct on a later rebuild — the
    // "через какое-то время" flip the operator sees. Resolve the record, then for EVERY
    // role re-render any such card into the clicker-only layout; for admins additionally
    // add the provisioning UI (Add device button + pending QR cards).
    void (async () => {
        const fid = appState.selectedFarmId;
        if (fid == null)
            return;
        const rec = await resolveFarmRecordById(fid);
        if (!rec || appState.selectedFarmId !== fid)
            return;
        // Cold deep-link straight to a farm grid by id: the name wasn't known at paint
        // time, so the grid still shows the right devices (matched by id) but under a
        // placeholder title. Adopt the resolved name and re-render once so the title,
        // duo-poll key and aggregator/duo detection all use it.
        if (appState.selectedFarm !== rec.name) {
            setSelectedFarm(rec.name, rec.id);
            renderFarmDevices();
            return;
        }
        // Duo farms have no relay devices — populate the grid from the DuoPlus API and
        // keep it refreshed while mounted. Nothing else on this page applies to them.
        if (isDuoFarm(rec.farm_kind)) {
            startDuoDevicePolling(rec.id, rec.name);
            return;
        }
        // GeeLark farms: same shape as duo — grid fed by the GeeLark API sync, kept
        // refreshed while mounted; nothing else on this page applies to them.
        if (isGeelarkFarm(rec.farm_kind)) {
            startGeelarkDevicePolling(rec.id, rec.name);
            return;
        }
        // AdsPower farms: grid fed by the agent's heartbeat (via GET …/adspower-devices),
        // polled while mounted. No relay stream — nothing else on this page applies.
        if (isAdspowerFarm(rec.farm_kind)) {
            startAdspowerDevicePolling(rec.id, rec.name);
            return;
        }
        // Mercado API farms: one card per MP account, fed by GET …/mercado-devices.
        if (isMercadoApiFarm(rec.farm_kind)) {
            startMercadoDevicePolling(rec.id, rec.name);
            return;
        }
        if (!isAggregatorFarm(rec.farm_kind))
            return;
        const g = document.getElementById('device-grid');
        if (!g || !document.body.contains(g))
            return;
        // A card built before we knew this was an aggregator still carries the Commands
        // button and lacks the per-phone delete/revoke one (aggregatorFarmId); re-render
        // those. Aggregator phones stream video like host devices, so we keep the player —
        // just skip already-converted cards, and don't rebuild a live stream mid-view.
        for (const d of devs) {
            const card = g.querySelector(`.device-card[data-udid="${CSS.escape(d.udid)}"]`);
            if (!card || !card.querySelector('.commands-btn'))
                continue; // already aggregator-styled
            if (inlineStreams.has(d.udid))
                continue; // don't disrupt a live stream
            card.replaceWith(makeDeviceCard(d, { aggregatorFarmId: rec.id }));
        }
        if (!can('farm.settings'))
            return;
        const header = document.querySelector('.farm-devs-header');
        if (header && document.body.contains(header) && !header.querySelector('#agg-add-device')) {
            const btn = document.createElement('button');
            btn.className = 'tbl-btn';
            btn.id = 'agg-add-device';
            btn.textContent = t('farms.add_device_btn');
            btn.addEventListener('click', () => showAddDeviceModal(rec.id, () => { }));
            header.appendChild(btn);
        }
        // Provisioned phones that haven't connected yet aren't in the live snapshot;
        // surface them on the grid as pending QR cards so they can be scanned/revoked.
        await injectAggregatorPendingCards(rec.id, new Set(devs.map((d) => d.udid)));
    })();
    if (devs.length === 0) {
        // Distinguish "still waiting for the first device-list snapshot" (e.g. right
        // after a page refresh) from a farm that genuinely has no live devices.
        grid.innerHTML = appState.devicesLoaded
            ? `<p class="empty">${t('farms.no_devices')}</p>`
            : `<p class="empty">${t('farms.loading_devices')}</p>`;
        updateArrows();
        return;
    }
    for (const d of devs)
        grid.appendChild(makeDeviceCard(d, resolveCardOpts(displayName)));
    // Enable per-user drag & drop reordering of this farm's grid (persisted in localStorage,
    // applied by farmDevices() → sortBySavedOrder on every render/poll).
    if (appState.selectedFarmId != null)
        wireGridDragReorder(grid, appState.selectedFarmId);
    // Cards just landed (and may carry images that change width as they decode);
    // sync now and once more after the layout/paint settles.
    updateArrows();
    requestAnimationFrame(updateArrows);
}
export function onDevicesChange(devices) {
    appState.allDevices = devices;
    appState.devicesLoaded = true;
    for (const udid of inlineStreams.keys()) {
        if (devices.get(udid)?.state !== 'device')
            stopInlineStream(udid);
    }
    onClickerDevicesChange();
    const route = parseRouteFromHash();
    if (route.page === 'workspace') {
        onWorkspaceDevicesChange();
        return;
    }
    if (route.page !== 'farms')
        return;
    const routeFarmId = route.farmId ?? null;
    setSelectedFarm(routeFarmId != null ? (farmNameById(routeFarmId) ?? null) : null, routeFarmId);
    // On the farm-settings page: don't re-render over it on device updates,
    // otherwise a background WS device change kicks the user to the device list.
    if (route.settings)
        return;
    if (routeFarmId != null) {
        // Duo farms aren't fed by the device-list poll — their grid is driven by the
        // separate DuoPlus sync (startDuoDevicePolling). Their phones never appear in
        // this device map, so a poll-driven renderFarmDevices() here would wipe and
        // rebuild the duo grid (the one blink seen on the first snapshot after a
        // refresh). Leave it untouched.
        if (isDuoFarmById(routeFarmId))
            return;
        // A drag-reorder is in progress: the freshest device data is already stored in
        // appState above, but rebuilding/patching the grid now would move DOM nodes out
        // from under the drag and snap the card back. Skip the render; the next poll after
        // dragend repaints with the just-saved order.
        if (isReordering())
            return;
        // Poll-driven render: only rebuild the grid (and its icon-button row) when
        // the structure-relevant fields actually changed. When only timestamps / run
        // state / claims moved, the shared engine patches the DOM in place.
        const farmName = appState.selectedFarm ?? '';
        const devs = farmDevices(devices, routeFarmId, farmName);
        const grid = document.getElementById('device-grid');
        if (grid && diffDeviceGrid(farmName, devs, grid, () => resolveCardOpts(farmName)) === 'patched')
            return;
        renderFarmDevices(); // resets the cache, then rebuilds the grid
        seedDeviceGridCache(farmName, devs);
    }
    else {
        // Poll-driven render of the farms landing list: only rebuild (toolbar with the
        // "+ Add farm" button and the team-selector pills, plus the rows) when the
        // structure key changed — otherwise leave the DOM in place so it doesn't flicker.
        onFarmsListPoll();
    }
}
